<template>
    <div class="analytics-dashboard container-fluid py-4">
        <div class="row">
            <div class="col-12">
                <h1 class="text-gradient mb-2">Account Activity</h1>
                <p class="text-muted mb-4">Active wallets, account operations, and top signers</p>

                <!-- Time Range Selector -->
                <div class="mb-4">
                    <date-range-picker
                        default-range="24h"
                        @change="handleDateRangeChange"
                    />
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Active Wallets</div>
                <div class="hero-value">{{ formatCompact(summary.totalUniqueWallets) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Transactions</div>
                <div class="hero-value">{{ formatCompact(summary.totalTransactions) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Avg Tx/Wallet</div>
                <div class="hero-value">{{ summary.avgTxPerWallet?.toFixed(1) || '0' }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Avg Accounts/Tx</div>
                <div class="hero-value">{{ summary.avgAccountsPerTx?.toFixed(1) || '0' }}</div>
            </div>
        </div>

        <!-- Main Charts Row -->
        <div class="analytics-row cols-2">
            <!-- Active Wallets Chart -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Active Wallets Over Time</div>
                    <div class="panel-subtitle">Unique transaction signers per period</div>
                </div>
                <div class="panel-body">
                    <canvas ref="activeWalletsChart"></canvas>
                </div>
            </div>

            <!-- Transaction Complexity Chart -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Volume & Complexity</div>
                    <div class="panel-subtitle">Transactions and average accounts per tx</div>
                </div>
                <div class="panel-body">
                    <canvas ref="txComplexityChart"></canvas>
                </div>
            </div>
        </div>

        <!-- Second Charts Row -->
        <div class="analytics-row cols-2 mt-4">
            <!-- Reads vs Writes Chart -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Account Operations</div>
                    <div class="panel-subtitle">Read vs Write account touches</div>
                </div>
                <div class="panel-body">
                    <canvas ref="readsWritesChart"></canvas>
                </div>
            </div>

            <!-- Account Writes Bar Chart -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Write Operations</div>
                    <div class="panel-subtitle">Writable account touches per period</div>
                </div>
                <div class="panel-body">
                    <canvas ref="accountWritesChart"></canvas>
                </div>
            </div>
        </div>

        <!-- Hot Writable Accounts -->
        <div class="analytics-row cols-1 mt-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Hot Writable Accounts</div>
                    <div class="panel-subtitle">Most contended accounts by compute usage</div>
                    <div class="panel-controls">
                        <select v-model="hotWritableSort" @change="loadHotWritable" class="form-select form-select-sm">
                            <option value="compute">By Compute</option>
                            <option value="fees">By Fees</option>
                        </select>
                    </div>
                </div>
                <div class="panel-body">
                    <div v-if="hotWritableLoading" class="text-center py-4">
                        <div class="spinner-border spinner-border-sm text-primary"></div>
                    </div>
                    <div v-else-if="hotWritable.length > 0" class="table-responsive">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th class="text-center" style="width: 50px">#</th>
                                    <th>Account</th>
                                    <th class="text-end">Compute Used</th>
                                    <th class="text-end">Share</th>
                                    <th class="text-end">Total Fees</th>
                                    <th class="text-end">Fees/Write</th>
                                    <th class="text-end">Transactions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(acc, index) in hotWritable" :key="acc.address">
                                    <td class="text-center text-muted">{{ index + 1 }}</td>
                                    <td>
                                        <div class="account-info">
                                            <a :href="`https://solscan.io/account/${acc.address}`" target="_blank"
                                               class="text-gradient" :title="acc.address">
                                                {{ acc.name || formatPubkey(acc.address) }}
                                            </a>
                                            <span v-if="acc.name" class="account-pubkey font-mono">
                                                {{ formatPubkey(acc.address) }}
                                            </span>
                                            <div v-if="acc.labels && acc.labels.length" class="account-labels">
                                                <span v-for="label in acc.labels.slice(0, 2)" :key="label"
                                                      class="label-badge" :class="`label-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`">
                                                    {{ label }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-end font-mono">{{ formatCompact(acc.totalCompute) }}</td>
                                    <td class="text-end">
                                        <div class="share-bar-cell">
                                            <div class="share-bar" :style="{ width: Math.min(acc.computeShare * 10, 100) + '%' }"></div>
                                            <span>{{ acc.computeShare.toFixed(2) }}%</span>
                                        </div>
                                    </td>
                                    <td class="text-end font-mono">{{ acc.totalFees.toFixed(2) }} SOL</td>
                                    <td class="text-end font-mono">{{ formatLamports(acc.feesPerWrite * 1e9) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(acc.transactionCount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                        No data available
                    </div>
                </div>
            </div>
        </div>

        <!-- Top Signers Table with Tabs -->
        <div class="analytics-row cols-1 mt-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top Signers</div>
                    <div class="panel-tabs">
                        <button
                            v-for="tab in signerTabs"
                            :key="tab.value"
                            class="tab-btn"
                            :class="{ active: signerSort === tab.value }"
                            @click="signerSort = tab.value; loadTopSigners()"
                        >
                            {{ tab.label }}
                        </button>
                    </div>
                    <div class="panel-controls">
                        <button class="compass-btn compass-btn--outline compass-btn--sm" @click="exportSignersToCSV">
                            Export CSV
                        </button>
                    </div>
                </div>
                <div class="panel-body">
                    <div v-if="signersLoading" class="text-center py-4">
                        <div class="spinner-border spinner-border-sm text-primary"></div>
                    </div>
                    <div v-else-if="topSigners.length > 0" class="table-responsive">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th class="text-center" style="width: 50px">#</th>
                                    <th>Signer</th>
                                    <th class="text-end">Total Fees</th>
                                    <th class="text-end">Transactions</th>
                                    <th class="text-end">Compute</th>
                                    <th class="text-end">Swap Vol (USD)</th>
                                    <th class="text-end">Programs</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(signer, index) in topSigners" :key="signer.address">
                                    <td class="text-center text-muted">{{ index + 1 }}</td>
                                    <td>
                                        <div class="account-info">
                                            <a :href="`https://solscan.io/account/${signer.address}`" target="_blank"
                                               class="text-gradient" :title="signer.address">
                                                {{ signer.name || formatPubkey(signer.address) }}
                                            </a>
                                            <span v-if="signer.name" class="account-pubkey font-mono">
                                                {{ formatPubkey(signer.address) }}
                                            </span>
                                            <div v-if="signer.labels && signer.labels.length" class="account-labels">
                                                <span v-for="label in signer.labels.slice(0, 2)" :key="label"
                                                      class="label-badge" :class="`label-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`">
                                                    {{ label }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-end">
                                        <span class="fw-bold">{{ signer.totalSpent.toFixed(2) }} SOL</span>
                                        <span class="text-muted small d-block">{{ signer.feeShare.toFixed(2) }}%</span>
                                    </td>
                                    <td class="text-end font-mono">{{ formatCompact(signer.transactionCount) }}</td>
                                    <td class="text-end">
                                        <span class="font-mono">{{ formatCompact(signer.totalCompute) }}</span>
                                        <span class="text-muted small d-block">{{ signer.computeShare.toFixed(2) }}%</span>
                                    </td>
                                    <td class="text-end font-mono">${{ formatCompact(signer.swapVolumeUsd) }}</td>
                                    <td class="text-end">
                                        <span class="compass-badge compass-badge--purple">{{ signer.programsUsed }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-center text-muted py-4">
                        No data available
                    </div>
                </div>
            </div>
        </div>

        <!-- Account Lifecycle Stats -->
        <div class="analytics-row cols-3 mt-4">
            <div class="analytics-panel compact">
                <div class="panel-body text-center">
                    <div class="stat-metric">
                        <div class="stat-icon stat-icon--green">+</div>
                        <div class="stat-label">Accounts Created</div>
                        <div class="stat-value">{{ formatCompact(summary.totalAccountsCreated) }}</div>
                    </div>
                </div>
            </div>
            <div class="analytics-panel compact">
                <div class="panel-body text-center">
                    <div class="stat-metric">
                        <div class="stat-icon stat-icon--red">-</div>
                        <div class="stat-label">Accounts Closed</div>
                        <div class="stat-value">{{ formatCompact(summary.totalAccountsClosed) }}</div>
                    </div>
                </div>
            </div>
            <div class="analytics-panel compact">
                <div class="panel-body text-center">
                    <div class="stat-metric">
                        <div class="stat-icon" :class="summary.netAccountsChange >= 0 ? 'stat-icon--green' : 'stat-icon--red'">
                            {{ summary.netAccountsChange >= 0 ? '↑' : '↓' }}
                        </div>
                        <div class="stat-label">Net Change</div>
                        <div class="stat-value" :class="summary.netAccountsChange >= 0 ? 'text-success' : 'text-danger'">
                            {{ summary.netAccountsChange >= 0 ? '+' : '' }}{{ formatCompact(summary.netAccountsChange) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { colors, getLineChartOptions, getBarChartOptions, getBaseOptions } from '../composables/useChartConfig';

export default {
    name: 'AccountAnalytics',

    data() {
        return {
            currentRange: '24h',
            currentInterval: '1h',
            summary: {
                totalUniqueWallets: 0,
                totalTransactions: 0,
                avgTxPerWallet: 0,
                avgAccountsPerTx: 0,
                totalReads: 0,
                totalWrites: 0,
                totalAccountsCreated: 0,
                totalAccountsClosed: 0,
                netAccountsChange: 0
            },
            // Hot writable accounts
            hotWritable: [],
            hotWritableLoading: false,
            hotWritableSort: 'compute',
            // Top signers
            topSigners: [],
            signersLoading: false,
            signerSort: 'fees',
            signerTabs: [
                { label: 'By Fees', value: 'fees' },
                { label: 'By Tx Count', value: 'tx_count' },
                { label: 'By Compute', value: 'compute' },
                { label: 'By USD Volume', value: 'usd_volume' }
            ],
            refreshInterval: null
        };
    },

    created() {
        this.charts = {};
    },

    mounted() {
        this.initCharts();
        this.loadData();

        this.refreshInterval = setInterval(() => {
            this.loadData();
        }, 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(chart => {
            if (chart) chart.destroy();
        });
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    },

    methods: {
        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.currentInterval = data.interval;
            this.loadData();
        },

        initCharts() {
            const baseOpts = getBaseOptions(true);
            const lineOpts = getLineChartOptions({ useDynamicColors: true });
            const barOpts = getBarChartOptions({ useDynamicColors: true });

            // Active Wallets Chart
            this.charts.activeWallets = new Chart(this.$refs.activeWalletsChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...lineOpts,
                    interaction: { mode: 'index', intersect: false },
                    scales: {
                        ...lineOpts.scales,
                        y: {
                            ...lineOpts.scales.y,
                            title: { display: true, text: 'Active Wallets' },
                            ticks: { ...lineOpts.scales.y.ticks, callback: v => this.formatCompact(v) }
                        }
                    },
                    plugins: {
                        ...lineOpts.plugins,
                        tooltip: {
                            callbacks: {
                                label: ctx => `${ctx.dataset.label}: ${this.formatCompact(ctx.parsed.y)}`
                            }
                        }
                    }
                }
            });

            // Transaction Complexity Chart (dual axis)
            this.charts.txComplexity = new Chart(this.$refs.txComplexityChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...lineOpts,
                    interaction: { mode: 'index', intersect: false },
                    scales: {
                        ...lineOpts.scales,
                        y1: {
                            type: 'linear',
                            position: 'left',
                            min: 0,
                            title: { display: true, text: 'Transactions' },
                            ticks: { callback: v => this.formatCompact(v) }
                        },
                        y2: {
                            type: 'linear',
                            position: 'right',
                            min: 0,
                            title: { display: true, text: 'Avg Accounts/Tx' },
                            grid: { drawOnChartArea: false }
                        }
                    }
                }
            });

            // Reads vs Writes Chart
            this.charts.readsWrites = new Chart(this.$refs.readsWritesChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...lineOpts,
                    interaction: { mode: 'index', intersect: false },
                    scales: {
                        ...lineOpts.scales,
                        y: {
                            ...lineOpts.scales.y,
                            stacked: false,
                            title: { display: true, text: 'Account Operations' },
                            ticks: { ...lineOpts.scales.y.ticks, callback: v => this.formatCompact(v) }
                        }
                    },
                    plugins: {
                        ...lineOpts.plugins,
                        tooltip: {
                            callbacks: {
                                label: ctx => `${ctx.dataset.label}: ${this.formatCompact(ctx.parsed.y)}`
                            }
                        }
                    }
                }
            });

            // Account Writes Bar Chart
            this.charts.accountWrites = new Chart(this.$refs.accountWritesChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...barOpts,
                    scales: {
                        ...barOpts.scales,
                        y: {
                            ...barOpts.scales.y,
                            title: { display: true, text: 'Write Operations' },
                            ticks: { ...barOpts.scales.y.ticks, callback: v => this.formatCompact(v) }
                        }
                    },
                    plugins: {
                        ...barOpts.plugins,
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: ctx => `Writes: ${this.formatCompact(ctx.parsed.y)}`
                            }
                        }
                    }
                }
            });
        },

        async loadData() {
            try {
                const response = await fetch(`/analytics/api/account-metrics?range=${this.currentRange}&interval=${this.currentInterval}`, {
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const data = await response.json();

                this.summary = data.summary || this.summary;

                // Update charts
                if (data.charts?.activeWallets && this.charts.activeWallets) {
                    this.charts.activeWallets.data = data.charts.activeWallets;
                    this.charts.activeWallets.update();
                }

                if (data.charts?.txComplexity && this.charts.txComplexity) {
                    this.charts.txComplexity.data = data.charts.txComplexity;
                    this.charts.txComplexity.update();
                }

                if (data.charts?.readsWrites && this.charts.readsWrites) {
                    this.charts.readsWrites.data = data.charts.readsWrites;
                    this.charts.readsWrites.update();
                }

                if (data.charts?.accountWrites && this.charts.accountWrites) {
                    this.charts.accountWrites.data = data.charts.accountWrites;
                    this.charts.accountWrites.update();
                }

                // Load tables (lazy load)
                this.loadHotWritable();
                this.loadTopSigners();

            } catch (error) {
                console.error('Error loading data:', error);
            }
        },

        async loadHotWritable() {
            this.hotWritableLoading = true;
            try {
                const response = await fetch(`/analytics/api/hot-writable?range=${this.currentRange}&limit=15&sort=${this.hotWritableSort}`);
                const data = await response.json();
                this.hotWritable = data.accounts || [];
            } catch (error) {
                console.error('Error loading hot writable accounts:', error);
            } finally {
                this.hotWritableLoading = false;
            }
        },

        async loadTopSigners() {
            this.signersLoading = true;
            try {
                const response = await fetch(`/analytics/api/top-signers?range=${this.currentRange}&limit=25&sort=${this.signerSort}`);
                const data = await response.json();
                this.topSigners = data.signers || [];
            } catch (error) {
                console.error('Error loading top signers:', error);
            } finally {
                this.signersLoading = false;
            }
        },

        exportSignersToCSV() {
            const rows = [
                ['Address', 'Total Fees (SOL)', 'Transactions', 'Compute', 'Swap Volume (USD)', 'Programs Used']
            ];

            this.topSigners.forEach(signer => {
                rows.push([
                    signer.address,
                    signer.totalSpent.toFixed(4),
                    signer.transactionCount,
                    signer.totalCompute,
                    signer.swapVolumeUsd.toFixed(2),
                    signer.programsUsed
                ]);
            });

            const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `top_signers_${this.signerSort}_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
        },

        formatPubkey(address) {
            if (!address || address.length < 12) return address;
            return `${address.slice(0, 4)}..${address.slice(-4)}`;
        },

        formatCompact(value) {
            if (!value) return '0';
            if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
            if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
            if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
            return value.toLocaleString();
        },

        formatLamports(value) {
            if (!value) return '0';
            if (value >= 1e9) return (value / 1e9).toFixed(4) + ' SOL';
            if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
            if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
            return Math.round(value).toLocaleString();
        }
    }
};
</script>

<style scoped>
/* Component-specific: Chart canvas minimum height */
.panel-body canvas {
    min-height: 280px;
}

/* Account info display with name and labels */
.account-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.account-pubkey {
    font-size: 0.7rem;
    color: var(--compass-text-secondary, #7d8590);
    opacity: 0.7;
}

.account-labels {
    display: flex;
    gap: 4px;
    margin-top: 2px;
    flex-wrap: wrap;
}

.label-badge {
    font-size: 0.55rem;
    padding: 1px 5px;
    border-radius: 3px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.02em;
}

/* Label color variants */
.label-cex { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
.label-kol { background: rgba(139, 92, 246, 0.2); color: #8b5cf6; }
.label-treasury { background: rgba(16, 185, 129, 0.2); color: #10b981; }
.label-market-maker { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.label-mm { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.label-liquidity-pool { background: rgba(6, 182, 212, 0.2); color: #06b6d4; }
.label-lp { background: rgba(6, 182, 212, 0.2); color: #06b6d4; }
.label-defi { background: rgba(168, 85, 247, 0.2); color: #a855f7; }
.label-nft { background: rgba(249, 115, 22, 0.2); color: #f97316; }
.label-exchange { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
.label-whale { background: rgba(234, 179, 8, 0.2); color: #eab308; }
</style>
